/**
 * Equipment-tab hook — client-side recomposition variant.
 *
 * Replaces the legacy `/equipment-tab` backend endpoint (deleted by spec
 * 2026-05-05-03). The hook composes:
 *   - useAssetEquipment      → descendants + inline properties
 *   - useAssetTypes          → trade UIDs + labels (kind/inherits_from from spec 02)
 *   - useAssetDerivedProperties → zone-A cards
 *   - useAsset               → parent name + asset-type code (for the response header)
 *
 * The exported public surface (hook signature, query-key shape, mutation
 * hooks) stays compatible with the previous implementation so existing UI
 * keeps working unchanged.
 *
 * Manual-override mutations write through the existing
 * `/assets/{uid}/properties/{propertyUid}` endpoints. Setting an override is
 * a PUT with `source_details.source = "manual"` and `priority = 10` — high
 * enough to win the `priority DESC NULLS LAST` ordering over the
 * derivation row (priority 0). Clearing an override is a PATCH that stamps
 * `end_date = now` on the active winner: the manual row drops out of the
 * non-expired filter, and the derivation underneath becomes visible again.
 * No bespoke `/manual` endpoints needed.
 */

import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAssetEquipment } from './useAssetEquipment'
import { useAssetDerivedProperties } from './useAssetDerivedProperties'
import { useAsset, useAssetTypes, useContracts } from './useQueries'
import { useAssetInspections } from './useComplianceApi'
import { recomposeEquipmentTab, type ContractLite } from '../lib/equipment/recomposeEquipmentTab'
import { apiBaseUrl } from '../api/client'
import type {
  EquipmentCondition,
  EquipmentTabResponse,
  SetManualDerivedPropertyRequest,
} from '../api/equipment-tab-types'

export interface EquipmentTabFilters {
  /** Free-text — name, model, brand. */
  q?: string
  /** Comma-joined list of conditions, e.g. `poor,end_of_life`. */
  conditions?: string
  /** Comma-joined list of trade codes, e.g. `trade_hvac,trade_lift`. */
  trades?: string
  /** Asset uid that scopes the location filter (currently a no-op in the
   *  client recomposition; left in the type to keep the call sites stable). */
  location_descendant_of?: string
}

interface RecomposedQueryResult {
  data: EquipmentTabResponse | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => void
}

function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem('authState')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { accessToken?: string }
    return parsed.accessToken || null
  } catch {
    return null
  }
}

function parseConditions(s?: string): EquipmentCondition[] {
  if (!s) return []
  const valid: EquipmentCondition[] = ['new', 'good', 'fair', 'poor', 'end_of_life', 'unassessed']
  return s
    .split(',')
    .map((x) => x.trim())
    .filter((c): c is EquipmentCondition => (valid as string[]).includes(c))
}

function parseTradeCodes(s?: string): string[] {
  if (!s) return []
  return s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
}

export function useEquipmentTab(
  org: string,
  assetUid: string | undefined,
  filters?: EquipmentTabFilters,
): RecomposedQueryResult {
  const equipmentQuery = useAssetEquipment(org, assetUid)
  const derivedQuery = useAssetDerivedProperties(org, assetUid || '')
  const { data: assetTypes = [] } = useAssetTypes(org)
  const { data: parentAsset } = useAsset(org, assetUid || '')

  // One request returns every contract linked anywhere in this asset's subtree,
  // with the linked assets + contract type hydrated — enough to light up the
  // Maintenance / Garantie columns without a request per equipment row.
  //
  // `lightweight: false` is load-bearing: lightweight contract types are typed
  // relations (contacts), not real contracts, and a contact pinned to an
  // equipment would otherwise light up its Maintenance icon. Same convention as
  // the Contracts list.
  const { data: subtreeContracts = [] } = useContracts(org, {
    assetUid,
    assetIncludeChildren: true,
    lightweight: false,
    include: ['assets', 'contract_type'],
    limit: 100,
    enabled: !!org && !!assetUid,
  })

  // Periodic inspections of every equipment under this asset, in one request
  // (rules of class periodic_inspection only — never the regulatory file).
  const { data: subtreeInspections } = useAssetInspections(org, assetUid, true)

  const data = useMemo<EquipmentTabResponse | undefined>(() => {
    if (!assetUid) return undefined
    if (equipmentQuery.isLoading || derivedQuery.isLoading) return undefined
    const parentTypeCode = (() => {
      const at = assetTypes.find((t) => t.uid === parentAsset?.asset_type_uid)
      return at?.code ?? ''
    })()
    return recomposeEquipmentTab({
      assetUid,
      assetName: parentAsset?.name ?? '',
      assetTypeCode: parentTypeCode,
      equipment: equipmentQuery.equipment,
      assetTypes: assetTypes as unknown as Parameters<typeof recomposeEquipmentTab>[0]['assetTypes'],
      derived: derivedQuery.cards,
      contracts: subtreeContracts as unknown as ContractLite[],
      inspections: subtreeInspections ?? [],
      filters: {
        conditions: parseConditions(filters?.conditions),
        trades: parseTradeCodes(filters?.trades),
        q: filters?.q,
      },
    })
  }, [
    assetUid,
    equipmentQuery.equipment,
    equipmentQuery.isLoading,
    derivedQuery.cards,
    derivedQuery.isLoading,
    assetTypes,
    parentAsset,
    subtreeContracts,
    subtreeInspections,
    filters?.conditions,
    filters?.trades,
    filters?.q,
  ])

  return {
    data,
    isLoading: equipmentQuery.isLoading || derivedQuery.isLoading,
    isError: equipmentQuery.isError || derivedQuery.isError,
    error: equipmentQuery.error ?? derivedQuery.error,
    refetch: () => {
      equipmentQuery.refetch()
      derivedQuery.refetch?.()
    },
  }
}

/**
 * Invalidate every cached `equipment-tab` query for the given asset. Kept as
 * an exported helper for backward compatibility — under the recomposed
 * implementation the hook's data flows from the underlying
 * useAssetEquipment / useAssetDerivedProperties caches, so this also
 * invalidates those.
 */
export function refreshEquipmentTab(
  queryClient: ReturnType<typeof useQueryClient>,
  org: string,
  assetUid: string,
) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey as readonly unknown[]
      if (key.length >= 3 && key[0] === 'asset-equipment' && key[1] === org && key[2] === assetUid) {
        return true
      }
      // Asset properties — covers the derived-properties cache.
      if (key.length >= 2 && key[0] === 'get') {
        const path = key[1]
        if (
          path === '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties' ||
          path === '/api/v1/organizations/{orgCode}/assets/{assetUid}'
        ) {
          const params = key.find((item) => item && typeof item === 'object' && 'params' in item) as
            | { params?: { path?: { orgCode?: string; assetUid?: string } } }
            | undefined
          if (params?.params?.path?.orgCode === org && params.params.path.assetUid === assetUid) {
            return true
          }
        }
      }
      return false
    },
  })
}

/**
 * Set a manual override on a property: PUT a new asset_data row with
 * `source = "manual"` + `priority = 10`. The high priority makes it win the
 * `priority DESC NULLS LAST` ordering used by ListAssetPropertyValues, so
 * subsequent reads return this row instead of the derivation underneath.
 */
async function putManualPropertyValue(
  org: string,
  assetUid: string,
  propertyUid: string,
  body: { value: unknown; reason?: string },
): Promise<void> {
  const token = getAuthToken()
  const url = `${apiBaseUrl}/api/v1/organizations/${encodeURIComponent(org)}/assets/${encodeURIComponent(assetUid)}/properties/${encodeURIComponent(propertyUid)}`
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`
  const sourceDetails: Record<string, unknown> = { source: 'manual' }
  if (body.reason) sourceDetails.reason = body.reason
  const res = await fetch(url, {
    method: 'PUT',
    headers,
    credentials: 'include',
    body: JSON.stringify({
      value: body.value,
      source_details: sourceDetails,
      priority: 10,
    }),
  })
  if (!res.ok) {
    throw new Error(`Set manual override failed: ${res.status} ${res.statusText}`)
  }
}

/**
 * Clear a manual override by stamping `end_date = now` on the currently
 * active row for this property — which, when an override is in place, IS
 * the manual row (priority 10 wins). After expiration, the derivation row
 * underneath (priority 0) becomes visible to readers again.
 */
async function expireActivePropertyValue(
  org: string,
  assetUid: string,
  propertyUid: string,
): Promise<void> {
  const token = getAuthToken()
  const url = `${apiBaseUrl}/api/v1/organizations/${encodeURIComponent(org)}/assets/${encodeURIComponent(assetUid)}/properties/${encodeURIComponent(propertyUid)}`
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(url, {
    method: 'PATCH',
    headers,
    credentials: 'include',
    body: JSON.stringify({ end_date: new Date().toISOString() }),
  })
  if (!res.ok && res.status !== 404) {
    throw new Error(`Clear manual override failed: ${res.status} ${res.statusText}`)
  }
}

/**
 * `key` here is the property *UID* that the recomposed hook surfaces on
 * each derived-property card (`property_uid`). Callers that previously
 * passed the property *code* must update to pass the UID — see
 * AssetDerivedPropertyCard.property_uid.
 */
export function useSetManualDerivedProperty(org: string, assetUid: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { key: string; body: SetManualDerivedPropertyRequest }) =>
      putManualPropertyValue(org, assetUid, vars.key, {
        value: vars.body.value,
        reason: vars.body.reason,
      }),
    onSuccess: () => {
      refreshEquipmentTab(queryClient, org, assetUid)
    },
  })
}

export function useRestoreComputedDerivedProperty(
  org: string,
  assetUid: string,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { key: string }) =>
      expireActivePropertyValue(org, assetUid, vars.key),
    onSuccess: () => {
      refreshEquipmentTab(queryClient, org, assetUid)
    },
  })
}
